<div class="p-20">
  <h3 class="page-header">SSO Settings</h3>
  <p>Configure Single Sign-On (SAML) authentication for your organization.</p>

  <!-- SSO Configuration Section -->
  <div class="sso-config-section">
    <h5>SSO Configuration</h5>
    <form [formGroup]="addSSOForm" *ngIf="addSSOForm">
      <div fxLayout="row wrap" class="margin-top-10" fxLayoutGap="10px">
        <div fxFlex="60%">
          <mat-label>Sign on URL / Identifier (Entity ID)</mat-label>
          <div><code>{{apiHost}}/v0/auth/{{authName?.value}}</code></div>
        </div>
        <div fxFlex="36%">
          <mat-label>Relay State</mat-label>
          <div><code>{{apiHost}}</code></div>
        </div>
        <div fxFlex="80%">
          <mat-label>Reply URL</mat-label>
          <div><code>{{apiHost}}/v0/auth/{{authName?.value}}/callback</code></div>
        </div>
      </div>
      
      <hr style="margin: 21px 0;" />
      
      <div fxLayout="row wrap" class="margin-top-10" fxLayoutGap="10px">
        <div fxFlex="32%">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>SAML Name</mat-label>
            <input matInput placeholder="SAML Name e.g. Company Name - SAML" formControlName="authTitle">
          </mat-form-field>
        </div>
        <div fxFlex="32%">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>SAML Identifier E.g. company-name/saml</mat-label>
            <input matInput placeholder="saml identifier e.g. company-name/saml" 
                   formControlName="authName" 
                   (change)="samlIdentifierChanged()"
                   (keyup)="validateSamlIdentifier()">
            <mat-hint *ngIf="!isSamlIdentifierValid" style="color: red;">
              <strong>Enter a URL SAFE Value</strong>
            </mat-hint>
          </mat-form-field>
        </div>
        <div fxFlex="32%">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Log Messages</mat-label>
            <mat-select formControlName="logMessages">
              <mat-option *ngFor="let logMessage of logMessageOpts" [value]="logMessage">
                {{logMessage}}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        <div fxFlex="66%">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Entry Point</mat-label>
            <input matInput placeholder="Entry Point" formControlName="entryPoint">
          </mat-form-field>
        </div>
        <div fxFlex="32%">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Issuer</mat-label>
            <mat-hint>This should match Entity ID in SAML. Override if client has entered a different Entity ID</mat-hint>
            <input matInput placeholder="certificate issuer, leave blank if not known" formControlName="issuer">
          </mat-form-field>
        </div>
        <div fxFlex="32%">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Audience</mat-label>
            <mat-hint>SAML audience restriction, typically the Entity ID of the service provider</mat-hint>
            <input matInput placeholder="Audience, leave blank if not known" formControlName="audience">
          </mat-form-field>
        </div>
        <div fxFlex="99%">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>X 509 Certificate</mat-label>
            <textarea matInput placeholder="X 509 Certificate" rows="20" formControlName='cert' style="font-family: 'Courier New', Courier, monospace;"></textarea>
          </mat-form-field>
        </div>
      </div>

      <!-- <div class="margin-top-20">
        <button mat-raised-button color="primary" (click)="saveSSO()" [disabled]="!addSSOForm.valid">
          Save SSO Settings
        </button>
      </div> -->
    </form>
  </div>

  <hr style="margin: 20px 0;" />


  <!-- XML File Upload Section -->
  <div class="xml-upload-section">
    <h5>Upload SAML Metadata XML File</h5>
    <div fxLayout="row wrap" class="margin-top-10" fxLayoutGap="10px">
      <div fxFlex="60%">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Select SAML XML File</mat-label>
          <input matInput readonly [value]="fileName" placeholder="Choose XML file...">
          <mat-icon matSuffix>file_upload</mat-icon>
        </mat-form-field>
      </div>
      <div fxFlex="20%">
        <button mat-raised-button color="primary" (click)="triggerFileInput()">
          Browse
        </button>
        <input 
          #fileInput
          id="xmlFileInput"
          type="file" 
          accept=".xml,text/xml" 
          style="display: none;"
          (change)="onFileSelected($event)">
      </div>
      <div fxFlex="20%">
        <button mat-raised-button color="accent" (click)="processUploadedXML()" [disabled]="!selectedFile">
          Extract from XML
        </button>
      </div>
    </div>
    <div fxLayout="row" *ngIf="selectedFile">
      <div fxFlex="100%">
        <button mat-button color="warn" (click)="clearSelectedFile()">
          <mat-icon>clear</mat-icon>
          Clear File
        </button>
      </div>
    </div>
  </div>

  <hr style="margin: 20px 0;" />

  <!-- URL-based Metadata Extraction Section -->
  <div class="url-extraction-section">
    <h5>Extract from Metadata URL</h5>
    <form [formGroup]="dummyForm" *ngIf="dummyForm">
      <div fxLayout="row wrap" class="margin-top-10" fxLayoutGap="10px">
        <div fxFlex="32%">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Sign on URL / Identifier Domain</mat-label>
            <div>
              <mat-select [(ngModel)]="authProvider.provider" formControlName="provider">
                <mat-option [value]="'okta'">Okta</mat-option>
                <mat-option [value]="'azure'">Azure AD</mat-option>
              </mat-select>
            </div>
          </mat-form-field>
        </div>
        <div fxFlex="32%">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Metadata - URL</mat-label>
            <input matInput placeholder="https://xxxx.okta.com/app/xxxxxx/sso/saml/metadata" 
                   [(ngModel)]="authProvider.metaDataUrl" formControlName="metaDataUrl">
          </mat-form-field>
        </div>
        <div fxFlex="32%">
          <button mat-raised-button (click)="processSSO()">Extract Values</button>
        </div>
      </div>
    </form>
  </div>



</div>
